import { useMemo, useState } from "react";
import { Icon } from "@iconify/react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { LoadingButton } from "@mui/lab";
import MuiConfirmationDialog from "../@material-extend/MuiConfirmationDialog";
import { useAuth } from "src/hooks/useAuth.hook";
import { useAccess } from "src/hooks/useAccess.hook";
import { useList, useMutate } from "src/hooks/useFetch.hook";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { FEATURE } from "src/util/enums";
import { fDateTime } from "src/utils/formatTime";
import {
  getTeamUrl,
  fleetOperatorInviteUrl,
  fleetOperatorUserIdUrl,
} from "src/react-query/endPoints";
import { toList } from "./fleet.constants";

const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || "");

const PortalAccessSection = ({ operatorId }) => {
  const { teamId } = useSelectedTeam();
  const { user } = useAuth();
  const isScopedOperator = user?.company?.fleetOperatorScope != null;
  // `user` hydrates asynchronously (Redux, after the profile fetch); gate on a
  // positive "profile loaded AND not scoped" signal so the CPO-only list fetch
  // doesn't fire during the loading window before scope is known.
  const isCpoAdmin = user?.company != null && !isScopedOperator;
  const canCreate = useAccess(FEATURE.FLEET_MANAGEMENT, "C");
  const canDelete = useAccess(FEATURE.FLEET_MANAGEMENT, "D");

  // Listing portal logins is CPO-only (403s for scoped operators). The section is
  // hidden for them anyway, so skip the fetch to avoid a spurious error toast.
  const { data } = useList("fleetOperatorUsers", isCpoAdmin, operatorId);
  const users = useMemo(() => toList(data), [data]);

  const { mutate, isLoading } = useMutate("fleetOperatorUsers", { id: operatorId });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [credentials, setCredentials] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  const openInvite = () => {
    setForm({ email: "", first_name: "", last_name: "" });
    setCredentials(null);
    setInviteOpen(true);
  };

  const onInvite = () => {
    mutate(
      {
        url: getTeamUrl(teamId, fleetOperatorInviteUrl(operatorId)),
        method: "POST",
        body: {
          email: form.email.trim(),
          first_name: form.first_name.trim() || undefined,
          last_name: form.last_name.trim() || undefined,
          send_email: true,
        },
      },
      {
        onSuccess: (res) => {
          const d = res?.data?.data ?? {};
          setCredentials({
            username: d.username,
            temporaryPassword: d.temporaryPassword,
          });
        },
      }
    );
  };

  const onRevoke = () => {
    mutate(
      {
        url: getTeamUrl(
          teamId,
          fleetOperatorUserIdUrl(operatorId, revoking?.cms_user_id)
        ),
        method: "DELETE",
      },
      {
        onSuccess: () => {
          setRevoking(null);
        },
      }
    );
  };

  const copyCredentials = () => {
    if (!credentials) return;
    navigator.clipboard
      ?.writeText(`${credentials.username} / ${credentials.temporaryPassword}`)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
  };

  // Scoped fleet-operator logins can't invite other logins — hide the section entirely.
  if (isScopedOperator) {
    return null;
  }

  return (
    <Box mt={4}>
      <Divider sx={{ mb: 3 }} />
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Typography variant="h6">Portal Access</Typography>
        {canCreate && (
          <Button
            variant="outlined"
            startIcon={<Icon icon="eva:person-add-outline" />}
            onClick={openInvite}
          >
            Invite Login
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Invite this fleet operator to log into the CMS. Invited logins currently
        have full access (same as an admin) — granular role restrictions arrive
        in a later release, so only invite trusted users for now.
      </Typography>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Login (email)</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Invited</TableCell>
              <TableCell>Last signed in</TableCell>
              {canDelete && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.cms_user_id}>
                <TableCell>{u.username}</TableCell>
                <TableCell>
                  {[u.first_name, u.last_name].filter(Boolean).join(" ") || "-"}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={u.login_enabled ? "Active" : "Revoked"}
                    color={u.login_enabled ? "success" : "default"}
                  />
                </TableCell>
                <TableCell>{fDateTime(u.invited_at) || "-"}</TableCell>
                <TableCell>{fDateTime(u.last_signed_in) || "Never"}</TableCell>
                {canDelete && (
                  <TableCell align="right">
                    {u.login_enabled && (
                      <Tooltip title="Revoke login">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRevoking(u)}
                        >
                          <Icon icon="eva:slash-outline" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
            {!users.length && (
              <TableRow>
                <TableCell colSpan={canDelete ? 6 : 5} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                    No portal logins yet.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={inviteOpen} onClose={() => setInviteOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Invite Fleet Operator Login</DialogTitle>
        <DialogContent>
          {credentials ? (
            <Box pt={1}>
              <Alert severity="success" sx={{ mb: 2 }}>
                Login created. Share these credentials now — the password is shown
                only once.
              </Alert>
              <TextField
                label="Username"
                value={credentials.username}
                fullWidth
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <TextField
                label="Temporary password"
                value={credentials.temporaryPassword}
                fullWidth
                margin="dense"
                InputProps={{ readOnly: true }}
              />
              <Button
                sx={{ mt: 1 }}
                startIcon={<Icon icon={copied ? "eva:checkmark-outline" : "eva:copy-outline"} />}
                onClick={copyCredentials}
              >
                {copied ? "Copied" : "Copy credentials"}
              </Button>
            </Box>
          ) : (
            <Grid container spacing={2} pt={1}>
              <Grid item xs={12}>
                <TextField
                  label="Email (login username)"
                  type="email"
                  fullWidth
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  error={!!form.email && !isValidEmail(form.email)}
                  helperText={
                    !!form.email && !isValidEmail(form.email)
                      ? "Enter a valid email"
                      : " "
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  value={form.first_name}
                  onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  value={form.last_name}
                  onChange={(e) => setForm({ ...form, last_name: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInviteOpen(false)}>
            {credentials ? "Done" : "Cancel"}
          </Button>
          {!credentials && (
            <LoadingButton
              variant="contained"
              loading={isLoading}
              disabled={!isValidEmail(form.email)}
              onClick={onInvite}
            >
              Send Invite
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>

      <MuiConfirmationDialog
        open={Boolean(revoking)}
        onClose={() => setRevoking(null)}
        title="Revoke Login"
        severity="error"
        contentText={`Revoke portal access for ${revoking?.username}? They will no longer be able to log in.`}
        closeText="Cancel"
        okText="Revoke"
        onOk={onRevoke}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default PortalAccessSection;
